import { Owned } from 'app/model/base/named';
import { ParameterValue } from 'app/model/base/parameter-value';
import { IAttribute } from 'app/model/base/annotation';
import { TEMPLATE_PROPERTY } from 'app/model/base/model';
import {
    AdapterContext, DATAFLOW_TEMPLATE_PROPERTY, ILogger, ParameterConfig, SupportedParameterTypes,
    convertParameterValue as shellLibConvertParameterValue, fillParameterValuesForResource as shellLibFillParameterValuesForResource
} from 'shell-models-lib';
import { loggerConstants } from 'app/logger/logger.constants';
import { FactoryResourcesAction } from 'app/logger/logger.factory-resources-action';
import { StringInterpolationResolver } from 'app/model/expressions/string-interpolation-resolver';

export { SupportedParameterTypes, stringifyParameterValue, convertParametersToModel } from 'shell-models-lib';

export function logParseNonObjectAsObjectEventFunc(invokedBy: string, loggerService: ILogger): void {
    loggerService?.logEvent(new FactoryResourcesAction(invokedBy, loggerConstants.Operation_ParseNonObjectAsObject, 1));
}

export function fillParameterValuesForResource(parameterList: ParameterValue[], adapterContext: AdapterContext, loggerService: ILogger): { [propertyName: string]: {} } {
    return shellLibFillParameterValuesForResource(parameterList, adapterContext, loggerService, logParseNonObjectAsObjectEventFunc);
}

/**
 * Convert parameter value to be the actual type. If the value is invalid, we will keep the original string value.
 */
export function convertParameterValue(value: string, valueType: string, invokedBy: string, loggerService: ILogger, doNotParseValueIntoObjectWithNonObjectTypeSetting: boolean = false): SupportedParameterTypes {
    return shellLibConvertParameterValue(value, valueType, invokedBy, loggerService, doNotParseValueIntoObjectWithNonObjectTypeSetting, logParseNonObjectAsObjectEventFunc);
}

export function hasParameterExpression(object: Owned): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const aObject = object as any;
    const store = aObject[TEMPLATE_PROPERTY] as Map<IAttribute, string>;
    if (store != null && store.size > 0) {
        return true;
    }
    return false;
}

export function listEntityParameterizedProperty(object: Owned): IAttribute[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const aObject = object as any;
    const store = aObject[TEMPLATE_PROPERTY] as Map<IAttribute, string>;
    if (store != null && store.size > 0) {
        return Array.from(store.keys());
    }
    return [];
}

export function wrapDataFlowPipelineExpression(parameters: ParameterValue[]): ParameterValue[] {
    if (!parameters?.length) {
        return [];
    }

    const paramsArrayCopy: ParameterValue[] = [];
        parameters.forEach((parameter) => {
            let expression = ParameterValue.VALUE.templateValue(parameter);
            let paramCopy: ParameterValue;
            if (!parameter.isExpression && parameter.parameter && parameter.parameter._type === 'string' && expression !== undefined) {
                // converting pipeline expression to interpolation format wrapped in quotes to match DSL string format
                // make sure it has interpolation format
                if (!(new StringInterpolationResolver(parameter, null).isOfType(expression))) {
                    if (expression.startsWith('@')) {
                        expression = expression.substr(1);
                    }
                    expression = `@{${expression}}`;
                }
                // wrapping in single quotes
                if (!expression.startsWith('\'')) {
                    expression = `'${expression}`;
                }
                if (!expression.endsWith('\'')) {
                    expression = `${expression}'`;
                }
                paramCopy = new ParameterValue();
                paramCopy.parameter = parameter.parameter;
                ParameterValue.VALUE.setTemplateValue(paramCopy, expression);
            }
            paramsArrayCopy.push(paramCopy || parameter);
        });
        return paramsArrayCopy;
}

export function unwrapDataFlowParams(parameters: ParameterValue[]): void {
    if (!parameters?.length) {
        return;
    }

    parameters.forEach((param) => {
            if (param.value) {
                const tempValue = param.value;
                ParameterValue.VALUE.setTemplateValue(param, param.value, DATAFLOW_TEMPLATE_PROPERTY);
                param.value = tempValue;
            } else {
                // remove single quotes and interpolation format
                let expression = ParameterValue.VALUE.templateValue(param);
                param.isExpression = true;
                if (expression && new StringInterpolationResolver(param, null).isOfType(expression) && expression.startsWith('\'') && expression.endsWith('\'')) {
                    param.isExpression = false;
                    expression = expression.substr(1);
                    expression = expression.substring(0, expression.length - 1);
                    if (expression.startsWith('@{') && expression.endsWith('}')) {
                        expression = `@${expression.substring(2, expression.length - 1)}`;
                    }
                    ParameterValue.VALUE.setTemplateValue(param, expression);
                }
            }
        });
}

export function convertParameterValuesToParameterConfigs(
    parameterValues: ParameterValue[],
    adapterContext: AdapterContext,
    loggerService: ILogger
): ParameterConfig[] {
    if (!parameterValues?.length) {
        return [];
    }
    const paramsCopy = wrapDataFlowPipelineExpression(parameterValues);
    const parameters = fillParameterValuesForResource(paramsCopy, adapterContext, loggerService);
    const parameterConfigs: ParameterConfig[] = [];
    if (parameters) {
        Object.keys(parameters).forEach((key) => {
            const paramConfig = new ParameterConfig();
            paramConfig.name = key;
            paramConfig.value = parameters[key];
            parameterConfigs.push(paramConfig);
        });
    }
    return parameterConfigs;
}